import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import {
  Box,
  Toolbar,
  List,
  CssBaseline,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  Menu,
  MenuItem,
  Button,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SpeedIcon from '@mui/icons-material/Speed';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EmailIcon from '@mui/icons-material/Email';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import SettingsIcon from '@mui/icons-material/Settings';
import { Person, Settings, Logout } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Dashboard from '../Dashboard';
import Users from '../Users';
import Feedbacks from '../Feedbacks';
import AppRequests from '../AppRequests';
import ControlPanel from '../ControlPanel';
import { Auth } from 'aws-amplify';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuthContext } from '../../context/authContext';

const drawerWidth = 250;

const drawerList = [
  {
    name: 'Dashboard',
    icon: <SpeedIcon />,
    component: <Dashboard />,
  },
  //   {
  //     name: 'Users',
  //     icon: <PeopleAltIcon />,
  //     component: <Users />,
  //   },
  {
    name: 'Feedbacks',
    icon: <EmailIcon />,
    component: <Feedbacks />,
  },
  {
    name: 'App Requests',
    icon: <AnnouncementIcon />,
    component: <AppRequests />,
  },
  //   {
  //     name: 'Control Panel',
  //     icon: <SettingsIcon />,
  //     component: <ControlPanel />,
  //   },
];

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const dividerMixin = () => ({
  backgroundColor: '#3D4D79',
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: '#fff',
  color: '#000',
  ...(!open && { left: '3.75%' }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: '#081737',
    color: '#ABB0CA',
  },
  '& .MuiTypography-root': {
    fontWeight: 'bold',
    fontSize: '16px',
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ selected }) => ({
  minWidth: 0,
  justifyContent: 'center',
  color: selected ? '#FFF' : '#ABB0CA',
  '&:hover': {
    color: '#FFF',
  },
}));

const UserMenu = ({ isDrawerOpen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { user } = useAuthContext();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      display={'flex'}
      alignItems={'center'}
      sx={{
        padding: '0 20px',
        margin: '10px 0',
        justifyContent: !isDrawerOpen ? 'center' : 'flex-start',
      }}
    >
      {/* User Profile Trigger */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: 'pointer', color: 'white' }}
        onClick={handleClick}
      >
        <Avatar alt="user-avatar" src="assets/images/placeholder-profile-sq.jpg" />
        {isDrawerOpen && (
          <Stack sx={{ paddingLeft: '10px' }}>
            <Typography sx={{ color: '#ABB0CA' }} variant="body1" fontWeight="bold">
              Welcome
            </Typography>
            <Typography sx={{ color: '#ABB0CA', textTransform: 'capitalize' }} variant="body2">
              {user.username.split('.')[0]}
            </Typography>
          </Stack>
        )}
        {/* {isDrawerOpen && (
					<Box sx={{ paddingLeft: "10px" }}>
						{open ? (
							<ExpandLessIcon sx={{ color: "#ABB0CA" }} />
						) : (
							<ExpandMoreIcon sx={{ color: "#ABB0CA" }} />
						)}
					</Box>
				)} */}
      </Stack>

      {/* <Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					elevation: 3,
					sx: {
						mt: 1,
						backgroundColor: "#fff",
						color: "#000",
					},
				}}
			>
				<MenuItem onClick={handleClose}>
					<ListItemIcon>
						<Person fontSize="small" sx={{ color: "#444444" }} />
					</ListItemIcon>
					{isDrawerOpen && (
						<Typography sx={{ color: "#444444", fontSize: "13px" }}>
							My Profile
						</Typography>
					)}
				</MenuItem>
				<MenuItem onClick={handleClose}>
					<ListItemIcon>
						<Settings fontSize="small" sx={{ color: "#444444" }} />
					</ListItemIcon>
					{isDrawerOpen && (
						<Typography sx={{ color: "#444444", fontSize: "13px" }}>
							Settings
						</Typography>
					)}
				</MenuItem>
			</Menu> */}
    </Box>
  );
};

const Home = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [selected, setSelected] = useState(drawerList[0].name);
  const [component, setComponent] = useState(drawerList[0].component);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleItemClick = (name, component) => {
    setSelected(name);
    setComponent(component);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[
              {
                marginRight: 5,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <Tooltip title="Logout">
              <IconButton onClick={() => Auth.signOut()}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          {open ? (
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
              <img
                src="assets/images/autodesk-logo-primary-rgb-white-large.png"
                alt="autodesk logo"
                width={'90%'}
              />
            </Box>
          ) : (
            <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
              <img
                src="assets/images/autodesk-only-logo-primary-rgb-white.png"
                alt="autodesk logo"
                width={'55%'}
              />
            </Box>
          )}
          {open && (
            <IconButton onClick={handleDrawerClose}>
              <StyledListItemIcon open={open}>
                {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </StyledListItemIcon>
            </IconButton>
          )}
        </DrawerHeader>
        <Divider sx={{ ...dividerMixin() }} />
        <UserMenu isDrawerOpen={open} />

        <Divider sx={{ ...dividerMixin() }} />
        <List>
          {drawerList.map(({ name, icon, component }) => (
            <ListItem key={name} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                    backgroundColor: selected === name ? '#1E88E5' : 'transparent',
                    borderLeft: `4px solid ${
                      selected === name ? theme.palette.active.main : 'transparent'
                    }`,
                    color: selected === name ? '#FFF' : '#ABB0CA',
                    '&:hover': {
                      borderLeft: `4px solid ${theme.palette.active.main}`,
                      color: '#FFF',
                    },
                  },
                  open
                    ? {
                        justifyContent: 'initial',
                      }
                    : {
                        justifyContent: 'center',
                      },
                ]}
                selected={selected === name}
                onClick={() => handleItemClick(name, component)}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: 'center',
                    },
                    open
                      ? {
                          mr: 3,
                        }
                      : {
                          mr: 'auto',
                        },
                  ]}
                >
                  <StyledListItemIcon open={open} selected={selected === name}>
                    {icon}
                  </StyledListItemIcon>
                </ListItemIcon>
                <ListItemText
                  primary={name}
                  sx={[
                    open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                        },
                  ]}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box
        sx={{
          width: open ? `calc(100% - 250px)` : `calc(100% - 60px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          padding: 3,
        }}
      >
        {component}
      </Box>
    </Box>
  );
};

export default Home;
